import {
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  multiFactor,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  type MultiFactorResolver,
  type User
} from 'firebase/auth';
import { doc, enableNetwork, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { getFirebaseAuth, getFirestoreDb } from '@/lib/firebase';
import { signInUserProfilePatch } from '@/lib/authUserProfile';
import { clearAllLastOrgIdHints } from '@finvox/shared/storage';
import {
  completeSignInNavigation,
  prepareFreshFirestoreSession,
  resetSessionAndGoToAuth,
  waitForAuthUser
} from '@/lib/sessionReset';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  mfaResolver: MultiFactorResolver | null;
  signIn: (email: string, password: string) => Promise<void>;
  completeMfaSignIn: (code: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  switchAccount: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

let networkEnablePromise: Promise<void> | null = null;

async function ensureFirestoreNetwork(): Promise<void> {
  if (networkEnablePromise) return networkEnablePromise;
  networkEnablePromise = enableNetwork(getFirestoreDb())
    .catch(() => {
      /* best-effort — avoid crashing login on SDK race */
    })
    .finally(() => {
      networkEnablePromise = null;
    });
  return networkEnablePromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const authHandoffRef = useRef(false);

  useEffect(() => {
    void (async () => {
      if (window.location.pathname === '/auth' && !getFirebaseAuth().currentUser) {
        await prepareFreshFirestoreSession();
      }
    })();

    const unsub = onAuthStateChanged(getFirebaseAuth(), (u) => {
      if (authHandoffRef.current) return;
      setUser(u);
      setLoading(false);
      if (u) void ensureFirestoreNetwork();
    });
    return unsub;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const auth = getFirebaseAuth();
    authHandoffRef.current = true;
    setLoading(true);
    try {
      if (auth.currentUser) {
        clearAllLastOrgIdHints();
        await signOut(auth);
        await waitForAuthUser(null);
      }
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await cred.user.getIdToken();
      await waitForAuthUser(cred.user.uid);
      await setDoc(
        doc(getFirestoreDb(), 'users', cred.user.uid),
        signInUserProfilePatch(email),
        { merge: true }
      );
      await ensureFirestoreNetwork();
      setMfaResolver(null);
      await completeSignInNavigation();
    } catch (err: unknown) {
      authHandoffRef.current = false;
      setLoading(false);
      const code = (err as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, err as never));
        return;
      }
      throw err;
    }
  }, []);

  const completeMfaSignIn = useCallback(
    async (code: string) => {
      if (!mfaResolver) throw new Error('No MFA challenge');
      authHandoffRef.current = true;
      setLoading(true);
      try {
        const hint = mfaResolver.hints[0];
        if (!hint) throw new Error('No MFA hint');
        const cred = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
        const result = await mfaResolver.resolveSignIn(cred);
        await waitForAuthUser(result.user.uid);
        await ensureFirestoreNetwork();
        setMfaResolver(null);
        await completeSignInNavigation();
      } catch (e) {
        authHandoffRef.current = false;
        setLoading(false);
        throw e;
      }
    },
    [mfaResolver]
  );

  const signUp = useCallback(async (email: string, password: string) => {
    const auth = getFirebaseAuth();
    authHandoffRef.current = true;
    setLoading(true);
    try {
      if (auth.currentUser) {
        clearAllLastOrgIdHints();
        await signOut(auth);
        await waitForAuthUser(null);
      }
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await cred.user.getIdToken();
      await waitForAuthUser(cred.user.uid);
      await setDoc(
        doc(getFirestoreDb(), 'users', cred.user.uid),
        {
          email: email.trim().toLowerCase(),
          memberships: [],
          createdAt: serverTimestamp()
        },
        { merge: true }
      );
      await ensureFirestoreNetwork();
      await completeSignInNavigation();
    } catch (e) {
      authHandoffRef.current = false;
      setLoading(false);
      throw e;
    }
  }, []);

  const signOutUser = useCallback(async () => {
    setMfaResolver(null);
    await resetSessionAndGoToAuth();
  }, []);

  const switchAccount = useCallback(async () => {
    setMfaResolver(null);
    await resetSessionAndGoToAuth();
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      mfaResolver,
      signIn,
      completeMfaSignIn,
      signUp,
      signOutUser,
      switchAccount
    }),
    [user, loading, mfaResolver, signIn, completeMfaSignIn, signUp, signOutUser, switchAccount]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function userHasMfa(user: User): boolean {
  return multiFactor(user).enrolledFactors.length > 0;
}
